import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LayoutGrid, Eye, EyeOff, UserPlus, Building2, Users } from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

export const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  // Fetch invitation info if invite token is present
  useEffect(() => {
    if (inviteToken) {
      fetchInviteInfo();
    }
  }, [inviteToken]);

  const fetchInviteInfo = async () => {
    setInviteLoading(true);
    try {
      const response = await fetch(`${API_URL}/invitations/by-token/${inviteToken}`);
      const data = await response.json();
      
      if (response.ok) {
        setInviteInfo(data);
      }
    } catch (err) {
      // Silently fail - user can still register
    } finally {
      setInviteLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const result = await register(name, email, password);
      
      // If there's an invite token, accept the invitation
      if (inviteToken && result?.token) {
        try {
          const acceptResponse = await fetch(`${API_URL}/invitations/accept-for-new-user/${inviteToken}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${result.token}`,
              'Content-Type': 'application/json'
            }
          });
          
          const acceptData = await acceptResponse.json();
          
          if (acceptData.joined) {
            toast.success(`Welcome! You've joined ${acceptData.target_name}`);
          }
        } catch (err) {
          // Silently fail - user is registered, just not auto-added
          console.error('Failed to auto-accept invitation:', err);
        }
      }
      
      // Ensure onboarding is not marked as complete for new users
      localStorage.removeItem('onboardingComplete');
      
      // Use window.location for hard navigation to prevent race condition with PublicRoute
      // This ensures the redirect happens before PublicRoute can intercept
      if (inviteToken) {
        window.location.href = `/onboarding?invite=${inviteToken}`;
      } else {
        window.location.href = '/onboarding';
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg bg-[#2563EB] flex items-center justify-center">
            <LayoutGrid className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-2xl text-[#1E293B]">BoardChat</span>
        </div>

        {/* Invitation Info Banner */}
        {inviteInfo && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                inviteInfo.target_type === 'hub' ? 'bg-purple-500' : 'bg-blue-500'
              }`}>
                {inviteInfo.target_type === 'hub' ? (
                  <Users className="w-5 h-5 text-white" />
                ) : (
                  <Building2 className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <p className="text-sm text-blue-800">
                  <span className="font-medium">{inviteInfo.inviter_name}</span> invited you to join
                </p>
                <p className="font-semibold text-blue-900">{inviteInfo.target_name}</p>
              </div>
            </div>
            {inviteInfo.message && (
              <p className="text-sm text-blue-700 mt-2 italic">"{inviteInfo.message}"</p>
            )}
          </div>
        )}

        {/* Register Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <h1 className="text-2xl font-bold text-[#1E293B] text-center mb-2">
            {inviteInfo ? 'Accept Invitation' : 'Create account'}
          </h1>
          <p className="text-[#64748B] text-center mb-6">
            {inviteInfo 
              ? 'Create your account to join the team' 
              : 'Start managing your projects today'}
          </p>

          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-[#1E293B]">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className="mt-1"
                required
                data-testid="register-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-[#1E293B]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#1E293B]">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-[#94A3B8] mt-1">Must be at least 6 characters</p>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white py-3"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? 'Creating account...' : (
                inviteInfo ? (
                  <>
                    <UserPlus size={18} className="mr-2" />
                    Sign up & Join
                  </>
                ) : 'Create account'
              )}
            </Button>
          </form>

          <p className="text-center text-[#64748B] text-sm mt-6">
            Already have an account?{' '}
            <Link 
              to={inviteToken ? `/login?invite=${inviteToken}` : '/login'} 
              className="text-[#2563EB] hover:underline font-medium"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
